import { useState, useRef, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { SetCameraOpen, SetCameraClosed, RestoreCameraState } from '../actions/cameraScannerActions';
import { isValidCameraLocation, isCameraOpenElsewhere, isMobileOrTablet } from './cameraLocationUtils';

export function useCameraScanner(location, options = {}) {
  const {
    navigation = null,
    onAfterClose = null,
    restoreOnMount = true,
    trackManualClose = true,
  } = options;

  const dispatch = useDispatch();
  const cameraScanner = useSelector(state => state.cameraScanner);

  const [showCameraScanner, setShowCameraScanner] = useState(false);
  const [camScannerEnabled, setCamScannerEnabled] = useState(false);
  const cameraManuallyClosedRef = useRef(false);
  const isFocusedRef = useRef(true);

  const fetchCamSetting = useCallback(async () => {
    try {
      if (!isMobileOrTablet()) {
        setCamScannerEnabled(false);
        return false;
      }
      const camEnabled = await AsyncStorage.getItem('camScannerEnabled');
      const value = camEnabled === 'true';
      setCamScannerEnabled(value);
      return value;
    } catch (error) {
      console.error('Error fetching camScannerEnabled:', error);
      return false;
    }
  }, []);

  useEffect(() => {
    if (restoreOnMount) {
      dispatch(RestoreCameraState());
    }
    fetchCamSetting();
  }, []);

  useEffect(() => {
    if (!navigation || !navigation.isFocused || !navigation.addListener) {
      isFocusedRef.current = true;
      return;
    }
    isFocusedRef.current = navigation.isFocused();
    const unsubFocus = navigation.addListener('focus', () => {
      isFocusedRef.current = true;
      fetchCamSetting();
    });
    const unsubBlur = navigation.addListener('blur', () => {
      isFocusedRef.current = false;
    });
    return () => {
      unsubFocus();
      unsubBlur();
    };
  }, [navigation, fetchCamSetting]);

  useEffect(() => {
    if (trackManualClose && cameraManuallyClosedRef.current && cameraScanner && !cameraScanner.isOpen) {
      cameraManuallyClosedRef.current = false;
    }

    const reduxOpen = cameraScanner && cameraScanner.isOpen;
    const validLoc = isValidCameraLocation(cameraScanner);
    const notManual = !cameraManuallyClosedRef.current;

    if (isFocusedRef.current && reduxOpen && validLoc && notManual && !showCameraScanner && camScannerEnabled) {
      setShowCameraScanner(true);
      if (cameraScanner.location !== location) {
        dispatch(SetCameraOpen(location));
      }
    }

    if (showCameraScanner && isCameraOpenElsewhere(cameraScanner, location)) {
      setShowCameraScanner(false);
    }

    if (!camScannerEnabled && showCameraScanner) {
      setShowCameraScanner(false);
      if (trackManualClose) cameraManuallyClosedRef.current = true;
      dispatch(SetCameraClosed());
    }
  }, [cameraScanner, showCameraScanner, camScannerEnabled, dispatch, location, trackManualClose]);

  const openCamera = useCallback(() => {
    setShowCameraScanner(true);
    cameraManuallyClosedRef.current = false;
    dispatch(SetCameraOpen(location));
  }, [dispatch, location]);

  const closeCamera = useCallback(() => {
    setShowCameraScanner(false);
    if (trackManualClose) cameraManuallyClosedRef.current = true;
    dispatch(SetCameraClosed());
    if (onAfterClose) onAfterClose();
  }, [dispatch, trackManualClose, onAfterClose]);

  return {
    showCameraScanner,
    setShowCameraScanner,
    camScannerEnabled,
    setCamScannerEnabled,
    openCamera,
    closeCamera,
    cameraManuallyClosedRef,
    fetchCamSetting,
  };
}
